import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button as AbwButton, Select as AbwSelect, Icon as AbwIcon } from '@Components/AbwaabPlus'
import { useGetEducationInfoQuery } from '@Features/Search/SearchApi/SearchApi'
import usePlatformFlags from '@Hooks/usePlatformFlags'

/**
 * The guest finder card — ONE component for every platform mode:
 * classic → "ابحث عن الفصل الافتراضي المخصص لك" targeting the الفصول tab;
 * courses-only → "ابحث عن الكورس المناسب لك" targeting الكورسات;
 * hybrid → shared wording targeting the الكل tab.
 */
const FindClassroomSection: React.FC = () => {
	const [t] = useTranslation('landingPage', { keyPrefix: 'courses' })
	const { t: tCourse } = useTranslation('standalone_course')
	const { t: tEdu } = useTranslation('educational_info')
	const { classroomsEnabled, coursesEnabled, bothEnabled } = usePlatformFlags()
	const navigate = useNavigate()
	const [educationType, setEducationType] = useState('')
	const [educationYear, setEducationYear] = useState('')

	const { data: eduRes } = useGetEducationInfoQuery()
	const eduTypes = eduRes?.data ?? []
	const typeOptions = eduTypes.map(ty => ({ value: String(ty.id), label: tEdu(`educational_type.${ty.name}`, { defaultValue: ty.name }) }))
	const selectedType = eduTypes.find(ty => String(ty.id) === educationType)
	const yearOptions = useMemo(() => (selectedType?.educationYears ?? []).map(y => ({ value: String(y.id), label: tEdu(`academic_year.${y.name}`, { defaultValue: y.name }) })), [selectedType, tEdu])

	const heading = bothEnabled
		? tCourse('find_any', { defaultValue: 'ابحث عن الفصل أو الكورس المناسب لك' })
		: coursesEnabled
		? tCourse('find_course', { defaultValue: 'ابحث عن الكورس المناسب لك' })
		: t('find_classroom', { defaultValue: 'ابحث عن الفصل الافتراضي المخصص لك' })

	const onSearch = () => {
		// hybrid lands on the combined الكل tab; single-platform modes go straight
		// to their own tab
		const params = new URLSearchParams({ tab: bothEnabled ? 'all' : coursesEnabled ? 'courses' : 'classrooms' })
		if (educationType) params.set('education_type', educationType)
		if (educationYear) params.set('education_year', educationYear)
		navigate(`/search?${params.toString()}`)
	}

	return (
		<Section>
			<Row>
				<IllustrationWrap>
					<CapCircle>
						<span className="cap">🎓</span>
						<span className="bulb">💡</span>
						<span className="book">
							<AbwIcon name="book-open" size={24} color="var(--blue)" />
						</span>
					</CapCircle>
				</IllustrationWrap>

				<Content>
					<TitleRow>
						<CapChip aria-hidden>🎓</CapChip>
						<Heading>{heading}</Heading>
					</TitleRow>
					<Controls>
						<AbwSelect value={educationType} onChange={v => { setEducationType(v); setEducationYear('') }} options={typeOptions} placeholder={t('education_system', { defaultValue: 'نظام التعليم' })} style={{ flex: 1 }} />
						<AbwSelect value={educationYear} onChange={setEducationYear} options={yearOptions} placeholder={t('academic_year', { defaultValue: 'السنة الدراسية' })} style={{ flex: 1 }} />
						<AbwButton variant="primary" size="lg" onClick={onSearch}>
							{t('search', { defaultValue: 'ابحث' })}
						</AbwButton>
					</Controls>
				</Content>
			</Row>
		</Section>
	)
}

export default FindClassroomSection

const Section = styled.section`
	padding: 8px 16px;
	@media (min-width: 1024px) {
		padding: 24px 16px 8px;
	}
`
const Row = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 28px;
	@media (min-width: 1024px) {
		flex-direction: row-reverse;
		gap: 48px;
	}
`
const IllustrationWrap = styled.div`
	/* decorative — the mobile design shows the finder card alone */
	flex: none;
	display: none;
	align-items: center;
	justify-content: center;
	@media (min-width: 1024px) {
		display: flex;
		flex: 0 0 40%;
	}
`
const CapCircle = styled.div`
	position: relative;
	/* phones: scale with viewport so it doesn't eat ~60% of a 375px screen */
	width: clamp(150px, 45vw, 220px);
	height: clamp(150px, 45vw, 220px);
	border-radius: 50%;
	background: var(--blue-08);
	display: flex;
	align-items: center;
	justify-content: center;
	@media (min-width: 768px) {
		width: 280px;
		height: 280px;
	}
	.cap {
		font-size: clamp(72px, 24vw, 96px);
		@media (min-width: 768px) {
			font-size: 120px;
		}
	}
	.bulb {
		position: absolute;
		top: 18px;
		right: 24px;
		width: 48px;
		height: 48px;
		border-radius: var(--r-lg);
		background: var(--yellow);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 22px;
		box-shadow: var(--sh-rest);
	}
	.book {
		position: absolute;
		bottom: 24px;
		left: 18px;
		width: 52px;
		height: 52px;
		border-radius: 50%;
		background: var(--surface-card);
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: var(--sh-hover);
	}
`
const Content = styled.div`
	flex: 1;
	text-align: start;
	width: 100%;
	/* mobile: the design's white finder card; desktop: plain content column */
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: var(--r-xl);
	padding: var(--space-4);
	@media (min-width: 1024px) {
		background: none;
		border: none;
		border-radius: 0;
		padding: 0;
	}
`
const TitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: var(--space-2);
`
const CapChip = styled.span`
	flex: none;
	width: 40px;
	height: 40px;
	border-radius: var(--r-lg);
	background: var(--blue-10);
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 20px;
	@media (min-width: 1024px) {
		display: none;
	}
`
const Heading = styled.h2`
	margin: 0;
	font: var(--fw-bold) var(--fs-body-lg)/1.4 var(--font-sans);
	color: var(--text-primary);
	@media (min-width: 1024px) {
		font-size: 32px;
	}
`
const Controls = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 12px;
	margin-top: 12px;
	@media (min-width: 1024px) {
		flex-direction: row;
		align-items: center;
		gap: 8px;
		margin: 20px 0 0;
		max-width: 520px;
		margin-inline-end: auto;
	}
`
